import type { GameplayPerformanceReport } from '../../shared/diagnostics';
import { onVisibilityChange, pageVisible } from '../platform/page-lifecycle';
import { reportGameplayPerformance } from '../platform/telemetry';

type Metric = 'inputFrame' | 'frameGap' | 'longTask' | 'wordAck' | 'submissionFeedback';
type Summary = NonNullable<GameplayPerformanceReport[Metric]>;
const SLOW_MS: Record<Metric, number> = {
  inputFrame: 50,
  frameGap: 50,
  longTask: 50,
  wordAck: 500,
  submissionFeedback: 50,
};

/** A 10% page sample, at most four 30s windows. No identifiers leave this object.
 * rAF measures a scheduling proxy, NOT paint completion or INP. Long tasks overlap
 * frame gaps: they are separate signals and must never be added together.
 */
export class GameplayPerformance {
  private readonly sampled: boolean;
  private active = false;
  private disposed = false;
  private frame: number | undefined;
  private observer: PerformanceObserver | undefined;
  private since = 0;
  private lastFrame: number | undefined;
  private inputAt: number | undefined;
  private feedbackAt: number | undefined;
  private windows = 0;
  private metrics: Partial<Record<Metric, Summary>> = {};
  // Only one in-flight acknowledgement sample; no unbounded word history.
  private pending: { round: string; word: string; at: number } | undefined;
  private readonly submittedKeys = new Set<string>(); // At most 64 first attempts per page.
  private stopVisibility: (() => void) | undefined;
  private readonly visibility = (visible: boolean) =>
    this.guard(() => {
      if (!visible) this.pause();
      else this.start();
    });

  constructor() {
    this.sampled = Math.random() < 0.1;
    this.guard(() => {
      if (this.sampled) this.stopVisibility = onVisibilityChange(this.visibility);
    });
  }

  setActive(active: boolean): void {
    this.guard(() => {
      if (active === this.active) return;
      this.active = active;
      if (active) this.start();
      else this.pause();
    });
  }

  input(timestamp: number): void {
    this.guard(() => {
      if (!this.measuring()) return;
      const now = performance.now();
      // Some older browsers use epoch timestamps rather than the performance clock.
      if (timestamp > now && timestamp >= performance.timeOrigin) timestamp -= performance.timeOrigin;
      if (!Number.isFinite(timestamp) || timestamp < this.since || timestamp > now) return;
      this.inputAt = this.inputAt === undefined ? timestamp : Math.min(timestamp, this.inputAt);
    });
  }

  submissionFeedback(): void {
    this.guard(() => {
      if (this.measuring()) this.feedbackAt ??= performance.now();
    });
  }

  submitted(round: string, word: string): void {
    this.guard(() => {
      if (!this.measuring()) return;
      const key = `${round}:${word}`;
      if (this.submittedKeys.has(key)) {
        // This bounded sampler matches by word, including id-less replies from
        // older servers. Omit repeated attempts rather than misattribute latency.
        if (this.pending?.round === round && this.pending.word === word) this.pending = undefined;
        return;
      }
      if (this.submittedKeys.size >= 64) return;
      this.submittedKeys.add(key);
      if (!this.pending) this.pending = { round, word, at: performance.now() };
    });
  }

  acknowledged(round: string, word: string): void {
    this.guard(() => {
      if (!this.measuring() || this.pending?.round !== round || this.pending.word !== word) return;
      this.add('wordAck', performance.now() - this.pending.at);
      this.pending = undefined;
    });
  }

  disconnected(): void {
    this.pending = undefined;
  }

  dispose(): void {
    this.guard(() => {
      this.pause();
      this.disposed = true;
      this.submittedKeys.clear();
      this.stopVisibility?.();
      this.stopVisibility = undefined;
    });
  }

  private measuring(): boolean {
    return this.sampled && !this.disposed && this.active && this.windows < 4 && pageVisible();
  }

  private start(): void {
    if (!this.measuring() || this.frame !== undefined) return;
    this.since = performance.now();
    this.lastFrame = undefined;
    this.inputAt = undefined;
    this.feedbackAt = undefined;
    this.frame = requestAnimationFrame(this.tick);
    // Safari may not expose longtask. rAF gaps remain the cross-browser fallback.
    if (
      typeof PerformanceObserver !== 'undefined' &&
      PerformanceObserver.supportedEntryTypes?.includes('longtask')
    ) {
      try {
        this.observer = new PerformanceObserver((list) =>
          this.guard(() => {
            if (!this.measuring()) return;
            for (const entry of list.getEntries()) {
              if (entry.startTime >= this.since && entry.startTime + entry.duration <= performance.now())
                this.add('longTask', entry.duration);
            }
          }),
        );
        this.observer.observe({ type: 'longtask', buffered: false });
      } catch {
        this.observer?.disconnect();
        this.observer = undefined;
      }
    }
  }

  private readonly tick = (): void =>
    this.guard(() => {
      const now = performance.now();
      this.frame = undefined;
      if (!this.measuring()) {
        this.pause();
        return;
      }
      if (this.lastFrame !== undefined) this.add('frameGap', now - this.lastFrame);
      this.lastFrame = now;
      if (this.inputAt !== undefined) {
        this.add('inputFrame', now - this.inputAt);
        this.inputAt = undefined;
      }
      if (this.feedbackAt !== undefined) {
        this.add('submissionFeedback', now - this.feedbackAt);
        this.feedbackAt = undefined;
      }
      // A missing acknowledgement expires; do not call it network-only latency.
      if (this.pending && now - this.pending.at > 60_000) this.pending = undefined;
      if (now - this.since >= 30_000) this.flush(now);
      if (this.measuring()) this.frame = requestAnimationFrame(this.tick);
      else this.pause();
    });

  private add(metric: Metric, duration: number): void {
    if (!Number.isFinite(duration) || duration < 0) return;
    const value = Math.min(60_000, Math.round(duration));
    const summary = (this.metrics[metric] ??= { count: 0, totalMs: 0, maxMs: 0, slowCount: 0 });
    if (summary.count >= 10_000) return;
    summary.count++;
    summary.totalMs += value;
    summary.maxMs = Math.max(summary.maxMs, value);
    if (value >= SLOW_MS[metric]) summary.slowCount++;
  }

  private flush(now: number): void {
    if (Object.keys(this.metrics).length && this.windows < 4) {
      this.windows++;
      reportGameplayPerformance({
        kind: 'gameplay_performance',
        windowMs: Math.min(60_000, Math.max(0, Math.round(now - this.since))),
        ...this.metrics,
      });
    }
    this.metrics = {};
    this.since = now;
    this.pending = undefined;
  }

  private pause(): void {
    if (this.frame !== undefined) cancelAnimationFrame(this.frame);
    this.frame = undefined;
    this.observer?.disconnect();
    this.observer = undefined;
    this.flush(performance.now());
    if (this.windows >= 4) {
      this.submittedKeys.clear();
      this.stopVisibility?.();
      this.stopVisibility = undefined;
    }
    this.lastFrame = undefined;
    this.inputAt = undefined;
    this.feedbackAt = undefined;
    this.pending = undefined;
  }

  private guard(action: () => void): void {
    try {
      action();
    } catch {
      /* Instrumentation is never on the game's failure path. */
    }
  }
}
